import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import GridLayout, {
  useContainerWidth,
  type LayoutItem,
} from "react-grid-layout";

import { useDrawdown } from "../api/hooks/useDrawdown";
import { useEquityCurve } from "../api/hooks/useEquityCurve";
import { usePnLByDay } from "../api/hooks/usePnLByDay";
import { useSummary } from "../api/hooks/useSummary";
import {
  CustomDashboardSelect,
  DEFAULT_DASHBOARD_ID,
} from "../components/dashboard/CustomDashboardSelect";
import { DateRangePresetBar } from "../components/dashboard/DateRangePresetBar";
import { FilterBar } from "../components/filters/FilterBar";
import { SaveLayoutButton } from "../components/dashboard/SaveLayoutButton";
import { AddWidgetSheet } from "../components/dashboard/widgets/AddWidgetSheet";
import { compactUpLeft } from "../components/dashboard/widgets/compact";
import { EmptyDashboardState } from "../components/dashboard/widgets/EmptyDashboardState";
import { readLayoutFromBlob } from "../components/dashboard/widgets/layoutAdapter";
import { renderWidget } from "../components/dashboard/widgets/registry";
import {
  DEFAULT_DASHBOARD_LAYOUT,
  EMPTY_LAYOUT,
  GRID_COLS,
  GRID_ROW_HEIGHT,
  WIDGET_DEFINITIONS,
  type DashboardLayout,
  type WidgetType,
} from "../components/dashboard/widgets/types";
import { WidgetChrome } from "../components/dashboard/widgets/WidgetChrome";
import { TourCard } from "../components/onboarding/TourCard";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { cn } from "../lib/cn";
import {
  DashboardFiltersContext,
  useDashboardFilterState,
} from "../lib/dashboardFilters";
import { usePageFilters } from "../lib/pageFilters";

function toRGL(layout: DashboardLayout): LayoutItem[] {
  return layout.widgets.map((w) => {
    const def = WIDGET_DEFINITIONS[w.type];
    return {
      i: w.id,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
      minW: def.minSize.w,
      minH: def.minSize.h,
    };
  });
}

function applyRGLLayout(
  layout: DashboardLayout,
  rglLayout: readonly LayoutItem[],
): DashboardLayout {
  const byId = new Map(rglLayout.map((l) => [l.i, l]));
  const updated = layout.widgets.map((w) => {
    const next = byId.get(w.id);
    if (next === undefined) return w;
    return { ...w, x: next.x, y: next.y, w: next.w, h: next.h };
  });
  return { ...layout, widgets: updated };
}

// Drop a freshly-added widget below the existing layout in column 0. RGL's
// vertical compactor pulls it up if there's room — this just guarantees no
// collision on insert.
function nextDropPosition(layout: DashboardLayout): { x: number; y: number } {
  if (layout.widgets.length === 0) return { x: 0, y: 0 };
  const maxBottom = layout.widgets.reduce(
    (acc, w) => Math.max(acc, w.y + w.h),
    0,
  );
  return { x: 0, y: maxBottom };
}

function generateWidgetId(type: WidgetType, layout: DashboardLayout): string {
  const taken = new Set(layout.widgets.map((w) => w.id));
  if (!taken.has(type)) return type;
  let suffix = 2;
  while (taken.has(`${type}-${suffix}`)) suffix += 1;
  return `${type}-${suffix}`;
}

type DashboardSearch = { edit?: "1" } & Record<string, unknown>;

// Skeleton mirror of the dashboard shell: header row, date-preset bar, and a
// grid of widget-sized cards. Shown on first load so the layout settles in
// place instead of flashing a "Loading…" line, then resolving to content.
function DashboardSkeleton() {
  return (
    <section
      className="space-y-4 p-6"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Loading dashboard</span>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant pb-3">
        <Skeleton className="h-9 w-40" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-44" />
          <Skeleton className="h-9 w-36" />
        </div>
      </div>
      <Skeleton className="h-9 w-full max-w-xl" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-48 w-full rounded-card" />
        ))}
      </div>
    </section>
  );
}

export function DashboardPage() {
  // Page-level fetch of the four core analytics queries delays first
  // paint until data is ready. Widgets call the same hooks; React Query
  // dedupes the duplicate subscriptions.
  const { filters: baseFilters, setFilters } = usePageFilters("dashboard");
  // Resolve the effective date window (defaulting to month-to-date) once here,
  // then share it with every widget via context so the grid renders the right
  // window on first paint instead of flashing all-time data.
  const dashboardFilters = useDashboardFilterState(baseFilters, setFilters);
  const filters = dashboardFilters.filters;
  const summary = useSummary(filters);
  const pnl = usePnLByDay(filters);
  const equity = useEquityCurve(filters);
  const drawdown = useDrawdown(filters);

  const search = useSearch({ strict: false }) as DashboardSearch;
  const navigate = useNavigate();
  const editMode = search.edit === "1";

  const setEditMode = useCallback(
    (next: boolean) => {
      void navigate({
        to: "/dashboard",
        search: (prev: Record<string, unknown>) => {
          const updated: Record<string, unknown> = { ...prev };
          if (next) updated.edit = "1";
          else delete updated.edit;
          return updated as never;
        },
        replace: true,
      });
    },
    [navigate],
  );

  // Cut (b): layout is held in component state. SaveLayoutButton serializes
  // the current layout via writeLayoutToBlob. Phase 5 wires the dashboard
  // picker — selecting a SavedDashboard replaces the layout in state.
  const [selectedDashboardId, setSelectedDashboardId] = useState<string>(
    DEFAULT_DASHBOARD_ID,
  );
  const [layout, setLayout] = useState<DashboardLayout>(() =>
    readLayoutFromBlob({ ...DEFAULT_DASHBOARD_LAYOUT }),
  );

  // Cut (c): "+ Add widget" FAB + side-sheet catalogue.
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  const existingWidgetTypes = useMemo(
    () => new Set<WidgetType>(layout.widgets.map((w) => w.type)),
    [layout],
  );

  const handleAddWidget = useCallback(
    (type: WidgetType) => {
      setLayout((prev) => {
        if (prev.widgets.some((w) => w.type === type)) return prev;
        const def = WIDGET_DEFINITIONS[type];
        const pos = nextDropPosition(prev);
        return {
          ...prev,
          widgets: [
            ...prev.widgets,
            {
              id: generateWidgetId(type, prev),
              type,
              x: pos.x,
              y: pos.y,
              w: def.defaultSize.w,
              h: def.defaultSize.h,
            },
          ],
        };
      });
      setAddWidgetOpen(false);
    },
    [],
  );

  const rglLayout = useMemo(() => toRGL(layout), [layout]);

  // react-grid-layout's useContainerWidth gives us a measured width without
  // the deprecated WidthProvider HOC (gone in v2.x).
  const { width, containerRef } = useContainerWidth();

  const isLoading =
    summary.isLoading || pnl.isLoading || equity.isLoading || drawdown.isLoading;
  const isError =
    summary.isError || pnl.isError || equity.isError || drawdown.isError;
  const firstError =
    summary.error ?? pnl.error ?? equity.error ?? drawdown.error;

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (
    isError ||
    summary.data === undefined ||
    pnl.data === undefined ||
    equity.data === undefined ||
    drawdown.data === undefined
  ) {
    return (
      <section className="p-6" role="alert">
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
          Dashboard unavailable
        </h1>
        <p className="mt-2 text-on-surface-variant">
          {firstError instanceof Error
            ? firstError.message
            : "Failed to load dashboard."}
        </p>
      </section>
    );
  }

  // Cut (d): empty-state takes over when there are no widgets and the user
  // is not actively editing. In edit mode the operator sees the empty grid +
  // Add widget button instead so they can build incrementally.
  if (layout.widgets.length === 0 && !editMode) {
    return (
      <section className="space-y-4 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant pb-3">
          <h1 className="font-display text-3xl tracking-tight text-on-surface">
            Dashboard
          </h1>
          <CustomDashboardSelect
            selectedId={selectedDashboardId}
            onSelect={(id, next) => {
              setSelectedDashboardId(id);
              setLayout(next);
            }}
          />
        </div>
        <EmptyDashboardState
          onApplyPreset={(preset) => {
            setLayout(preset);
          }}
          onStartBlank={() => {
            setLayout(EMPTY_LAYOUT);
            setEditMode(true);
          }}
        />
      </section>
    );
  }

  return (
    <DashboardFiltersContext.Provider value={dashboardFilters}>
      <section
        className={cn(
          "space-y-4 p-6 transition-colors",
          editMode && "bg-surface-container-low/30",
        )}
      >
        <TourCard />
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-outline-variant pb-3">
          <div className="flex items-end gap-3">
            <h1 className="font-display text-3xl tracking-tight text-on-surface">
              Dashboard
            </h1>
            <span className="mb-1 text-sm text-on-surface-variant">
              Your trading at a glance
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CustomDashboardSelect
              selectedId={selectedDashboardId}
              onSelect={(id, next) => {
                setSelectedDashboardId(id);
                setLayout(next);
              }}
            />
            {editMode && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAddWidgetOpen(true);
                }}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add widget
              </Button>
            )}
            {editMode && (
              <SaveLayoutButton
                layout={layout}
                onSaved={(savedId) => {
                  setSelectedDashboardId(savedId);
                }}
              />
            )}
            <Button
              type="button"
              variant={editMode ? "default" : "outline"}
              onClick={() => {
                setEditMode(!editMode);
              }}
              aria-pressed={editMode}
            >
              {editMode ? "Done" : "Edit dashboard"}
            </Button>
          </div>
        </div>

        <FilterBar
          filters={filters}
          setFilters={setFilters}
          showAccount
          showTag
          leading={<DateRangePresetBar />}
          aria-label="Dashboard filters"
        />

        <div
          ref={containerRef}
          className={cn(
            "relative rounded-md",
            editMode &&
              "bg-[length:8px_8px] bg-[radial-gradient(circle,_var(--color-outline-variant)_1px,_transparent_1px)]",
          )}
          data-testid="dashboard-grid-container"
        >
          <GridLayout
            className="layout"
            layout={rglLayout}
            width={width || 1200}
            gridConfig={{
              cols: GRID_COLS,
              rowHeight: GRID_ROW_HEIGHT,
              margin: [12, 12],
              containerPadding: [0, 0],
            }}
            dragConfig={{
              enabled: editMode,
              handle: ".widget-drag-handle",
            }}
            resizeConfig={{
              enabled: editMode,
              handles: ["se"],
            }}
            onLayoutChange={(next) => {
              setLayout((prev) => applyRGLLayout(prev, next));
            }}
          >
            {layout.widgets.map((widget) => (
              <div key={widget.id}>
                <WidgetChrome
                  title={WIDGET_DEFINITIONS[widget.type].title}
                  editMode={editMode}
                  onRemove={() => {
                    setLayout((prev) => ({
                      ...prev,
                      widgets: compactUpLeft(
                        prev.widgets.filter((w) => w.id !== widget.id),
                        GRID_COLS,
                      ),
                    }));
                  }}
                >
                  {renderWidget(widget.type)}
                </WidgetChrome>
              </div>
            ))}
          </GridLayout>
        </div>

        <AddWidgetSheet
          open={addWidgetOpen}
          onOpenChange={setAddWidgetOpen}
          existingTypes={existingWidgetTypes}
          onSelect={handleAddWidget}
        />
      </section>
    </DashboardFiltersContext.Provider>
  );
}
